import { defineStore } from 'pinia'
import { DEFAULT_EMBED_VIDEO_URL } from '@/utils/mediaEmbed'

const defaultCustomization = () => ({
  id: null,
  name: '',
  state: '',
  theme: {},
  formOptions: {
    phoneRequired: false,
  },
  media: {
    heroImageUrl: '',
    videoSource: 'vimeo',
    videoUrl: DEFAULT_EMBED_VIDEO_URL,
    overlayColor: null,
    overlayOpacity: null,
    header: {
      color: '#FFFFFF',
      textColor: '#0F172A',
      heightPx: 105,
    },
  },
  email: {
    subject: 'Your Tax Bill Report is Ready',
    bodyHtml: '<p>Hello [FirstName],</p><p>Your Tax Bill report is ready for review.</p>',
    attachPdfReport: true,
    mergeTagGroups: [
      {
        category: 'Visitor',
        tags: [
          { label: 'First name', token: '[FirstName]' },
          { label: 'Last name', token: '[LastName]' },
        ],
      },
      {
        category: 'Company',
        tags: [
          { label: 'Company name', token: '[CompanyName]' },
          { label: 'Company website', token: '[CompanyWebsite]' },
          { label: 'Disclosure', token: '[DisclousureEmail]' },
        ],
      },
      {
        category: 'Advisor',
        tags: [
          { label: 'Full name', token: '[AdvisorName]' },
          { label: 'Phone', token: '[AdvisorPhone]' },
          { label: 'Email', token: '[AdvisorEmail]' },
          { label: 'Meet URL', token: '[AdvisorLeadCaptureMeetUrl]' },
        ],
      },
    ],
  },
})

function stripLegacyMergeTagMarkup(value) {
  if (value === null || value === undefined) return ''

  const rawValue = String(value).trim()
  const textValue = rawValue
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .trim()
    .toLowerCase()

  if (textValue === 'null') return ''

  return String(value || '')
    .replace(/<span\b[^>]*data-token=["']?([^"' >]+)["']?[^>]*>.*?<\/span>/gi, '$1')
    .replace(/<span\b[^>]*class=["'][^"']*email-merge-tag[^"']*["'][^>]*>(.*?)<\/span>/gi, '$1')
    .replace(/<span\b[^>]*contenteditable=["']false["'][^>]*>(.*?)<\/span>/gi, '$1')
    .replace(/[\uFEFF\u200B]/g, '')
}

export const useLeadGeneratorStore = defineStore('leadGenerator', {
  state: () => ({
    company: {
      company_name: '',
      company_logo: '',
      company_website: '',
      advisor_email: '',
      lead_allow: false,
      meeting_url: '',
      userFullName: '',
      phone: '',
      lead_capture_disclosure: '',
      gtm_code: '',
    },
    user: {
      first_name: '',
      last_name: '',
      age: 0,
      taxDeferred: 0,
      taxRate: 0,
      taxPaid: 0,
      totalTaxesConversion: 0,
      email: '',
      phone: '',
      advisorId: '',
      proposalId: '',
    },
    // Tracks whether the lead-notification email has already been sent
    // for the current proposal so a page reload doesn't re-trigger it.
    emailSent: false,
    customizationLoaded: false,
    customization: defaultCustomization(),
    // Incremented after every successful hero image upload so any component
    // watching the URL can append a cache-buster without changing the stored URL.
    heroImageCacheBust: 0,
  }),

  actions: {
    ensureCustomizationDefaults() {
      const defaults = defaultCustomization()

      this.customization = {
        ...defaults,
        ...(this.customization || {}),
        theme: {
          ...defaults.theme,
          ...(this.customization?.theme || {}),
        },
        formOptions: {
          ...defaults.formOptions,
          ...(this.customization?.formOptions || {}),
        },
        media: {
          ...defaults.media,
          ...(this.customization?.media || {}),
          header: {
            ...defaults.media.header,
            ...(this.customization?.media?.header || {}),
          },
        },
        email: {
          ...defaults.email,
          ...(this.customization?.email || {}),
          bodyHtml: stripLegacyMergeTagMarkup(this.customization?.email?.bodyHtml || defaults.email.bodyHtml),
          mergeTagGroups: this.customization?.email?.mergeTagGroups?.length
            ? this.customization.email.mergeTagGroups
            : defaults.email.mergeTagGroups,
        },
      }
    },
    applyCustomizationPayload(payload = {}) {
      const defaults = defaultCustomization()

      this.customization = {
        ...defaults,
        id: payload.id ?? defaults.id,
        name: payload.name || defaults.name,
        state: payload.state || defaults.state,
        theme: payload.theme || defaults.theme,
        formOptions: {
          ...defaults.formOptions,
          ...(payload.formOptions || {}),
        },
        media: {
          ...defaults.media,
          ...(payload.media || {}),
          header: {
            ...defaults.media.header,
            ...(payload.media?.header || {}),
          },
        },
        email: {
          ...defaults.email,
          ...(payload.email || {}),
          bodyHtml: stripLegacyMergeTagMarkup(payload.email?.bodyHtml || defaults.email.bodyHtml),
          mergeTagGroups: defaults.email.mergeTagGroups,
        },
      }

      this.customizationLoaded = true
    },
    markCustomizationLoaded() {
      this.ensureCustomizationDefaults()
      this.customizationLoaded = true
    },
    /** Bump after a re-upload to the same S3 key so template <img> re-fetches. */
    bumpHeroImageCacheBust() {
      this.heroImageCacheBust = Date.now()
    },
    /** Call this before routing to /analysis so each new submission is fresh. */
    resetForNewSubmission() {
      this.emailSent = false
    },
  },

  persist: {
    // Use sessionStorage: data survives a page reload within the same tab
    // but is automatically cleared when the browser session ends.
    storage: sessionStorage,
  },
})
